import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { modifier } from 'ember-modifier';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import type { WithBoundArgs } from '@glint/template';

interface NavTabsItemComponentSignature {
  Args: {
    label?: string;
    route: string;
    model?: Record<string, unknown>;
    select: Element;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLAnchorElement;
}

const navTabsItemBaseClasses = [
  'whitespace-nowrap',
  'border-b',
  'border-b-2',
  'px-1',
  'py-4',
  'text-sm',
  'font-medium',
];

const navTabsItemDefaultClasses = [
  'border-transparent',
  'text-gray-500',
  'hover:border-gray-300',
  'hover:text-gray-700',
];

const navTabsItemActiveClasses = ['active', 'border-indigo-500', 'text-indigo-600'];

class NavTabsItemComponent extends Component<NavTabsItemComponentSignature> {
  @service declare router: RouterService;

  get isActive() {
    return this.router.currentRouteName === this.args.route;
  }

  isOptionSelected = modifier((element: HTMLOptionElement, [active]: [boolean]): void => {
    if (active) {
      element.setAttribute('selected', 'selected');
    } else {
      element.removeAttribute('selected');
    }
  });

  onInsertOption = modifier((): void => {
    const { select } = this.args;
    select?.querySelector('option[data-placeholder]')?.remove();
  });

  get linkClass() {
    if (this.isActive) {
      return [...navTabsItemBaseClasses, ...navTabsItemActiveClasses].join(' ');
    }
    return [...navTabsItemBaseClasses, ...navTabsItemDefaultClasses].join(' ');
  }

  <template>
    {{#if @model}}
      <LinkTo @route={{@route}} @model={{@model}} class={{this.linkClass}}>
        {{#if (has-block)}}
          {{yield}}
        {{else}}
          {{@label}}
        {{/if}}
      </LinkTo>
    {{else}}
      <LinkTo @route={{@route}} class={{this.linkClass}}>
        {{#if (has-block)}}
          {{yield}}
        {{else}}
          {{@label}}
        {{/if}}
      </LinkTo>
    {{/if}}
    {{#if @select}}
      {{#in-element @select insertBefore=null}}
        <option {{this.isOptionSelected this.isActive}} {{this.onInsertOption}} value={{@route}}>
          {{#if (has-block)}}
            {{yield}}
          {{else}}
            {{@label}}
          {{/if}}
        </option>
      {{/in-element}}
    {{/if}}
  </template>
}

interface NavTabsSignature {
  Args: {
    model?: any;
  };
  Blocks: {
    default: [{ item: WithBoundArgs<typeof NavTabsItemComponent, 'select'> }];
  };
  Element: HTMLElement;
}

const navTabsSelectClasses = [
  'block',
  'md:hidden',
  'w-full',
  'rounded-md',
  'border',
  'border-gray-300',
  'py-2',
  'pl-3',
  'pr-10',
  'focus:border-indigo-500',
  'focus:outline-none',
  'focus:ring-indigo-500',
  'sm:text-sm',
];

class NavTabsComponent extends Component<NavTabsSignature> {
  @service declare router: RouterService;
  @tracked select?: Element;

  @action
  setSelect(element: Element): void {
    this.select = element;
  }

  onInsertSelect = modifier((element: HTMLElement) => {
    this.setSelect(element);
  });

  @action
  onSelectChange(e: Event): void {
    const target = e.target as HTMLSelectElement;
    const route = target.value;
    this.router.transitionTo(route, this.args.model);
  }

  get selectClass(): string {
    return navTabsSelectClasses.join(' ');
  }

  <template>
    {{! Leave div. Can cause layout issues because the label is in the dom, but sr only}}
    <div>
      <div ...attributes>
        <label for="tabs" class="sr-only">Nav tabs</label>
        {{! template-lint-disable require-input-label}}
        <select
          {{on "change" this.onSelectChange}}
          {{this.onInsertSelect}}
          name="tabs"
          class={{this.selectClass}}
        >
          {{! in-element helper doesn't seem to properly inject content in to an empty select }}
          <option class="hidden" data-placeholder>placeholder</option>
        </select>
      </div>
      <div class="hidden md:block" ...attributes>
        <div class="border-b border-gray-200">
          <nav class="-mb-px flex space-x-8" area-label="Tabs">
            {{yield (hash item=(component NavTabsItemComponent select=this.select))}}
          </nav>
        </div>
      </div>
    </div>
  </template>
}

export default NavTabsComponent;
